import type { Manifest } from '@clawharbor/manifest';

/**
 * The onboarding API contract, as the console consumes it.
 *
 *   POST /api/onboard              {url, ownership}   -> {jobId, tenantId}
 *   GET  /api/onboard/:id/events           -> SSE of {phase, message, pct}
 *   GET  /api/onboard/:id                  -> JobRecord
 *   GET  /api/onboard/:id/manifest         -> Manifest
 *   POST /api/onboard/:id/publish          -> {webUrl, mcpUrl}
 *
 * The server is written by another agent in parallel, so everything past the
 * four documented fields is treated as OPTIONAL enrichment. If the stream only
 * ever carries {phase, message, pct}, the console still renders the full
 * clearance ledger — it recovers the per-capability verdicts by parsing the log
 * lines the crawler pipeline already emits (see `parseProbeLine`). If the
 * server sends structured fields instead, those win. Neither side has to change
 * for the other to work.
 *
 * The gate additionally speaks two smaller contracts, both same-origin and
 * cookie-carried:
 *
 *   GET  /api/auth/me                      -> {principal} | 401
 *   POST /api/auth/login           {email} -> 202 {sent, devLink?}
 *   GET  /api/ownership/:tenant            -> {claim}
 *   POST /api/ownership/:tenant/challenge  -> the three proofs
 *   POST /api/ownership/:tenant/verify     -> {verified, attempts}
 */

export const PHASES = ['intake', 'observe', 'brand', 'synthesize', 'probe', 'assemble'] as const;
export type Phase = (typeof PHASES)[number];

/**
 * What the person submitting a crawl says about their standing.
 *
 * The API has accepted this field since the route was written, and the console
 * never sent it — so every crawl went out as `third-party`, and a business
 * crawling its own site got a manifest headed "Unofficial. Not affiliated
 * with…" itself. `permitted` is the server's own word for a proven claim and is
 * never something a visitor asserts, so it is not offered here.
 */
export type Ownership = 'self-owned' | 'third-party';

export interface StartResponse {
  jobId: string;
  tenantId: string;
}

export interface PublishResponse {
  webUrl: string;
  mcpUrl: string;
}

export type JobStatus = 'queued' | 'running' | 'ready' | 'published' | 'failed';

export interface JobRecord {
  jobId: string;
  tenantId: string;
  url: string;
  status: JobStatus;
  error?: string;
  webUrl?: string;
  mcpUrl?: string;
}

/**
 * One SSE frame. `phase`, `message` and `pct` are the documented fields; the
 * rest are read when present and inferred from `message` when not.
 */
export interface ProgressEvent {
  phase?: string;
  message?: string;
  pct?: number;
  /** Terminal frames. The console also treats stream close after assemble as done. */
  status?: JobStatus;
  error?: string;
  /** Structured probe verdict, if the server chooses to emit one. */
  capability?: string;
  verified?: boolean;
  rows?: number;
  passRate?: number;
  samples?: number;
}

/** A capability's verdict at the probe gate. The product's central claim. */
export interface Clearance {
  id: string;
  verdict: 'cleared' | 'turned-back';
  rows: number;
  passRate: number;
  samples?: number;
  /** Why it was turned back, verbatim from the prober. */
  reason?: string;
  /** Wall-clock arrival, used only for stagger-free ordering. */
  seq: number;
}

export interface StageState {
  key: Phase;
  /** 1..6 — the crawler prints "── 3/6 brand", so the console counts the same way. */
  ordinal: number;
  label: string;
  /** What this stage is actually doing, in the operator's language. */
  gloss: string;
  status: 'waiting' | 'running' | 'done' | 'failed';
  notes: string[];
  startedAt?: number;
  endedAt?: number;
}

/**
 * Who is asking, as `GET /api/auth/me` reports it.
 *
 * `operator` is only ever answered to the reload-secret header, which a browser
 * never attaches on its own; the console meets it only where someone has wired
 * that header in by hand. `account` is the magic-link session in the cookie.
 */
export type Principal =
  | { kind: 'operator' }
  | {
      kind: 'account';
      accountId: string;
      email: string;
      tenants: Array<{ tenantId: string; role: 'owner' }>;
    };

export interface LoginResponse {
  sent: boolean;
  /**
   * Present only when the host's email provider is `log`, i.e. development.
   * The API cannot produce it with a real provider, so rendering it is safe as
   * long as it is labelled for what it is.
   */
  devLink?: string;
}

export type ProofMethod = 'dns' | 'file' | 'meta';

/**
 * `POST /api/ownership/:tenant/challenge`, the fields the gate renders.
 *
 * The API writes the instructions; the console shows them. Restating "add a
 * TXT record" in the console's own words would hand a person two versions of
 * the same procedure to reconcile, and the API's version is the one the
 * verifier actually checks against.
 */
export interface OwnershipChallenge {
  challengeId: string;
  tenantId: string;
  domain: string;
  origin: string;
  token: string;
  expiresAt: string;
  instructions: {
    dns: { recordName: string; labelOnly: string; recordType: string; value: string; howTo: string };
    file: { url: string; path: string; contentType: string; body: string; howTo: string };
    meta: { url: string; metaName: string; tag: string; howTo: string };
  };
  next: {
    verifyUrl: string;
    statusUrl: string;
    propagation: string;
    answerBy: string;
    tokenShownOnce: string;
  };
  note?: string;
}

export interface OwnershipAttempt {
  method: ProofMethod;
  ok: boolean;
  code: string;
  detail: string;
}

/** `POST /api/ownership/:tenant/verify`. A miss is a 200 with `verified: false`. */
export interface OwnershipVerification {
  verified: boolean;
  method: ProofMethod | null;
  attempts: OwnershipAttempt[];
  /** The API's own "what to do next" sentence, on a miss. */
  nextStep?: string;
  /** On a hit: what changed, with the API's reminder that nothing was published. */
  effect?: { ownership: string; disclaimerRequired: boolean; published: boolean; note: string };
}

/** `GET /api/ownership/:tenant` — is there a claim, and is it current. */
export interface OwnershipStatus {
  live: boolean;
  claim: {
    domain: string;
    method: ProofMethod;
    state: string;
    current: boolean;
    expiresAt: string;
    daysUntilExpiry: number;
  } | null;
  /**
   * A challenge already issued and not yet answered. Its token was shown once,
   * on the response that issued it, and is not here — so an open challenge is
   * something to verify against, never something to display.
   */
  openChallenge: { id: string; domain: string; origin: string; expiresAt: string; attempts: number } | null;
}

/**
 * Why `/publish` said no, verbatim.
 *
 * `code` is the API's own word for it (`ownership_unproven`,
 * `third_party_requires_operator`, `unauthorized`, `publish_disabled`) and
 * `message` is the sentence it sent — written to say what to do next, not
 * merely that the door is shut. The gate renders both and adds nothing the API
 * did not say.
 */
export interface PublishFailure {
  message: string;
  code: string | null;
}

export interface OnboardApi {
  readonly mode: 'live' | 'demo';
  /** `ownership` is what the person asserted on the landing screen. */
  start(url: string, ownership?: Ownership): Promise<StartResponse>;
  /** Returns an unsubscribe function. `done` fires once, after the last frame. */
  events(
    jobId: string,
    handlers: {
      onEvent: (e: ProgressEvent) => void;
      onDone: () => void;
      onError: (message: string) => void;
    },
  ): () => void;
  manifest(jobId: string): Promise<Manifest>;
  publish(jobId: string): Promise<PublishResponse>;
}

/**
 * The account and ownership calls the gate makes, kept apart from `OnboardApi`
 * because the recorded replay implements the crawl and nothing else: there is
 * no account service behind a demo, and a replay that answered `me()` with an
 * invented principal would be the console signing somebody in to nothing.
 */
export interface AccountApi {
  /** `null` is "not signed in" — a 401, a network failure, or the replay alike. */
  me(): Promise<Principal | null>;
  /**
   * `next` is where the mailed link should land — a same-origin path the server
   * validates against its own allowlist before it goes anywhere near a link.
   * The console sends it so a person who signed in to start a crawl returns to
   * the console holding the address they typed, rather than to the dashboard
   * with nothing to show for the detour.
   */
  login(email: string, next?: string): Promise<LoginResponse>;
  ownership(tenantId: string): Promise<OwnershipStatus>;
  challenge(tenantId: string): Promise<OwnershipChallenge>;
  verify(tenantId: string, challengeId?: string): Promise<OwnershipVerification>;
}

/**
 * The account-aware controls, added to the frame's `Actions` from here.
 *
 * `actions.ts` belongs to the frame and is shared by every screen, so these
 * members are declared as an augmentation rather than by editing that file:
 * the frame keeps compiling with the `Actions` it already hands the screens,
 * and TypeScript still checks every call site against the full shape. esbuild
 * strips the block; nothing of it reaches the bundle. Folding the members into
 * `actions.ts` is a cut-and-paste whenever that file is next open.
 */
declare module './actions.js' {
  interface Actions {
    /** Landing: "This is my site" / "Someone else's site". */
    setOwnership(value: Ownership): void;
    setLoginEmail(value: string): void;
    /** POST /api/auth/login with the typed address. */
    login(): void;
    /** POST /api/ownership/:tenant/challenge — the three proofs, shown once. */
    requestProofs(): void;
    /** POST /api/ownership/:tenant/verify against the open challenge. */
    verifyOwnership(): void;
  }
}
